import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useAssociateActions from '../../../hooks/APIHooks/users/useAssociateActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import { AppointmentRequest } from '../../../types/appointmentRequests';
import { Associate } from '../../../types/associates';
import { Client, User } from '../../../types/users';
import { showPicture } from '../../../utils/commonFunctions';
import {
  calculateTimeSlotDurationInMinutes,
  showDurationInHoursMinutes,
  showTimeIn12HrsFormat,
} from '../../../utils/timeAndDate';
import AcademiaRating from '../AcademiaRating';
import AcademiaSectionWrapper from '../AcademiaSectionWrapper';
import CardBadge from '../Badges/CardBadge';
import AssociateCardCount from '../Cards/AssociateCardCount';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AppointmentDetailsModal = ({
  appointment,
  handleModalClose,
}: {
  appointment: AppointmentRequest;
  handleModalClose: () => void;
}) => {
  // destructuring the appointment object here
  const {
    additional_info,
    client,
    associate_full_name,
    credits_cost,
    credits_per_slot,
    date,
    start_time,
    end_time,
    appointment_buffer_time,
    slot_duration,
    associate,
  } = appointment || {};

  // calculating total duration in minutes here
  const totalDuration = calculateTimeSlotDurationInMinutes(
    start_time,
    end_time
  );

  // integration of react hooks here
  const [associateDetails, setAssociateDetails] = useState<Associate | null>(
    null
  );
  const [clientDetails, setClientDetails] = useState<Client | null>(null);

  // integration of custom API hooks here
  const { getAssociateDetailsById } = useAssociateActions('', associate);
  const { getClientDetailsHandler } = useClientActions('', undefined, client);

  useEffect(() => {
    if (getAssociateDetailsById.data?.is_success) {
      setAssociateDetails(getAssociateDetailsById.data.data);
    }
  }, [getAssociateDetailsById.data]);

  useEffect(() => {
    if (getClientDetailsHandler.data?.is_success) {
      setClientDetails(getClientDetailsHandler.data.data);
    }
  }, [getClientDetailsHandler.data]);

  console.log(
    'ASSOCIATE DETAILS: ',
    getAssociateDetailsById.data?.data,
    appointment
  );

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // rendering appointment details modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[700px]'>
      <ModalTitle titleText='appointment details' />

      <div>
        {currentUserType === 'client' ? (
          associateDetails?.id ? (
            <AcademiaSectionWrapper sectionTitle='Associate Info'>
              <div className='flex flex-col md:flex-row items-center justify-between'>
                {associateDetails.profile_picture ? (
                  <img
                    className='size-24 rounded-full object-cover'
                    src={showPicture(associateDetails.profile_picture)}
                    alt={`avatar of ${associateDetails.username}`}
                  />
                ) : (
                  <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
                    <FaUser className='size-14 text-gray-300' />
                  </div>
                )}

                <div className='hidden md:block w-1/2'>
                  <div className='grid grid-cols-3'>
                    <AssociateCardCount
                      count={associateDetails.total_appointments}
                      text='Appointments'
                    />
                    <AssociateCardCount
                      count={associateDetails.feedback_count}
                      text='Reviews'
                    />
                    <AssociateCardCount
                      count={associateDetails.total_languages}
                      text='Languages'
                    />
                  </div>
                </div>

                <div className='block md:hidden'>
                  <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                    {associate_full_name}
                  </h3>

                  <AcademiaRating
                    size={30}
                    ratingValue={associateDetails.associate_rating}
                    isReadonly
                  />
                </div>
              </div>

              <div className='flex flex-col md:flex-row items-center justify-between mt-3 md:mt-0'>
                <div className='hidden md:block'>
                  <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                    {associate_full_name}
                  </h3>

                  <AcademiaRating
                    size={30}
                    ratingValue={associateDetails.associate_rating}
                    isReadonly
                  />
                </div>

                <div className='w-full md:w-1/2'>
                  <p className='text-xl text-center font-medium text-base-dark-green'>
                    {credits_per_slot} Credits
                    <span className='text-sm font-normal text-base-orange'>
                      /slot
                    </span>
                  </p>

                  <div className='flex items-center justify-center gap-2 mt-2'>
                    {associateDetails.language_names.map((language, index) => (
                      <CardBadge key={index} badgeText={language} />
                    ))}
                  </div>
                </div>

                <div className='block md:hidden w-full mt-3'>
                  <div className='grid grid-cols-3'>
                    <AssociateCardCount
                      count={associateDetails.total_appointments}
                      text='Appointments'
                    />
                    <AssociateCardCount
                      count={associateDetails.feedback_count}
                      text='Reviews'
                    />
                    <AssociateCardCount
                      count={associateDetails.total_languages}
                      text='Languages'
                    />
                  </div>
                </div>
              </div>
            </AcademiaSectionWrapper>
          ) : null
        ) : (currentUser as User).role_name === 'Admin' ? (
          <>
            {associateDetails?.id ? (
              <AcademiaSectionWrapper sectionTitle='Associate Info'>
                <div className='flex flex-col md:flex-row items-center justify-between'>
                  {associateDetails.profile_picture ? (
                    <img
                      className='size-24 rounded-full object-cover'
                      src={showPicture(associateDetails.profile_picture)}
                      alt={`avatar of ${associateDetails.username}`}
                    />
                  ) : (
                    <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
                      <FaUser className='size-14 text-gray-300' />
                    </div>
                  )}

                  <div className='hidden md:block w-1/2'>
                    <div className='grid grid-cols-3'>
                      <AssociateCardCount
                        count={associateDetails.total_appointments}
                        text='Appointments'
                      />
                      <AssociateCardCount
                        count={associateDetails.feedback_count}
                        text='Reviews'
                      />
                      <AssociateCardCount
                        count={associateDetails.total_languages}
                        text='Languages'
                      />
                    </div>
                  </div>

                  <div className='block md:hidden'>
                    <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                      {associate_full_name}
                    </h3>

                    <AcademiaRating
                      size={30}
                      ratingValue={associateDetails.associate_rating}
                      isReadonly
                    />
                  </div>
                </div>

                <div className='flex flex-col md:flex-row items-center justify-between mt-3 md:mt-0'>
                  <div className='hidden md:block'>
                    <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                      {associate_full_name}
                    </h3>

                    <AcademiaRating
                      size={30}
                      ratingValue={associateDetails.associate_rating}
                      isReadonly
                    />
                  </div>

                  <div className='w-full md:w-1/2'>
                    <p className='text-xl text-center font-medium text-base-dark-green'>
                      {credits_per_slot} Credits
                      <span className='text-sm font-normal text-base-orange'>
                        /slot
                      </span>
                    </p>

                    <div className='flex items-center justify-center gap-2 mt-2'>
                      {associateDetails.language_names.map(
                        (language, index) => (
                          <CardBadge key={index} badgeText={language} />
                        )
                      )}
                    </div>
                  </div>

                  <div className='block md:hidden w-full mt-3'>
                    <div className='grid grid-cols-3'>
                      <AssociateCardCount
                        count={associateDetails.total_appointments}
                        text='Appointments'
                      />
                      <AssociateCardCount
                        count={associateDetails.feedback_count}
                        text='Reviews'
                      />
                      <AssociateCardCount
                        count={associateDetails.total_languages}
                        text='Languages'
                      />
                    </div>
                  </div>
                </div>
              </AcademiaSectionWrapper>
            ) : null}

            {clientDetails?.id ? (
              <AcademiaSectionWrapper sectionTitle='Client Info'>
                <div className='flex flex-col md:flex-row items-center justify-between'>
                  {clientDetails.profile_picture ? (
                    <img
                      className='size-24 rounded-full object-cover'
                      src={showPicture(clientDetails.profile_picture)}
                      alt={`avatar of ${clientDetails.username}`}
                    />
                  ) : (
                    <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
                      <FaUser className='size-14 text-gray-300' />
                    </div>
                  )}

                  <div className='hidden md:block w-1/2'>
                    <div className='grid grid-cols-3'>
                      <AssociateCardCount
                        count={clientDetails.appointment_count}
                        text='Appointments'
                      />
                      <AssociateCardCount
                        count={clientDetails.feedback_count}
                        text='Reviews'
                      />
                      <AssociateCardCount
                        count={clientDetails.associate_count}
                        text='Associates'
                      />
                    </div>
                  </div>

                  <div className='block md:hidden'>
                    <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                      {clientDetails.client_full_name}
                    </h3>
                  </div>
                </div>

                <div className='flex flex-col md:flex-row items-center justify-between mt-3 md:mt-0'>
                  <div className='hidden md:block'>
                    <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                      {clientDetails.client_full_name}
                    </h3>
                  </div>

                  <div className='block md:hidden w-full mt-3'>
                    <div className='grid grid-cols-3'>
                      <AssociateCardCount
                        count={clientDetails.appointment_count}
                        text='Appointments'
                      />
                      <AssociateCardCount
                        count={clientDetails.feedback_count}
                        text='Reviews'
                      />
                      <AssociateCardCount
                        count={clientDetails.associate_count}
                        text='Associates'
                      />
                    </div>
                  </div>
                </div>
              </AcademiaSectionWrapper>
            ) : null}
          </>
        ) : clientDetails?.id ? (
          <AcademiaSectionWrapper sectionTitle='Client Info'>
            <div className='flex flex-col md:flex-row items-center justify-between'>
              {clientDetails.profile_picture ? (
                <img
                  className='size-24 rounded-full object-cover'
                  src={showPicture(clientDetails.profile_picture)}
                  alt={`avatar of ${clientDetails.username}`}
                />
              ) : (
                <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
                  <FaUser className='size-14 text-gray-300' />
                </div>
              )}

              <div className='hidden md:block w-1/2'>
                <div className='grid grid-cols-3'>
                  <AssociateCardCount
                    count={clientDetails.appointment_count}
                    text='Appointments'
                  />
                  <AssociateCardCount
                    count={clientDetails.feedback_count}
                    text='Reviews'
                  />
                  <AssociateCardCount
                    count={clientDetails.associate_count}
                    text='Associates'
                  />
                </div>
              </div>

              <div className='block md:hidden'>
                <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                  {clientDetails.client_full_name}
                </h3>
              </div>
            </div>

            <div className='flex flex-col md:flex-row items-center justify-between mt-3 md:mt-0'>
              <div className='hidden md:block'>
                <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                  {clientDetails.client_full_name}
                </h3>
              </div>

              <div className='block md:hidden w-full mt-3'>
                <div className='grid grid-cols-3'>
                  <AssociateCardCount
                    count={clientDetails.appointment_count}
                    text='Appointments'
                  />
                  <AssociateCardCount
                    count={clientDetails.feedback_count}
                    text='Reviews'
                  />
                  <AssociateCardCount
                    count={clientDetails.associate_count}
                    text='Associates'
                  />
                </div>
              </div>
            </div>
          </AcademiaSectionWrapper>
        ) : null}

        <AcademiaSectionWrapper sectionTitle='Appointment Info'>
          <div className='space-y-2 md:space-y-1'>
            <div className='flex flex-col md:flex-row items-center gap-1 text-base-dark-green'>
              <p className='w-[180px] text-center md:text-left'>
                Appointment Date
              </p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>{moment(date).format('DD-MM-YYYY')}</p>
            </div>

            <div className='flex flex-col md:flex-row items-center gap-1 text-base-purple'>
              <p className='w-[180px] text-center md:text-left'>
                Appointment Time
              </p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>
                {`${showTimeIn12HrsFormat(
                  start_time
                )} - ${showTimeIn12HrsFormat(end_time)}`}{' '}
                <span className='text-base-cyan'>
                  [
                  {Math.ceil(
                    totalDuration / (slot_duration + appointment_buffer_time)
                  ) || 1}{' '}
                  {Math.ceil(
                    totalDuration / (slot_duration + appointment_buffer_time)
                  ) > 1
                    ? 'slots'
                    : 'slot'}{' '}
                  in total]
                </span>
              </p>
            </div>

            <div className='flex flex-col md:flex-row items-center gap-1 text-base-light-blue'>
              <p className='w-[180px] text-center md:text-left'>
                Appointment Duration
              </p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>
                {showDurationInHoursMinutes(totalDuration)}
              </p>
            </div>

            <div className='flex flex-col md:flex-row items-center gap-1 text-base-orange'>
              <p className='w-[180px] text-center md:text-left'>Total Cost</p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>{credits_cost || 0} Credits</p>
            </div>
          </div>
        </AcademiaSectionWrapper>

        <AcademiaSectionWrapper sectionTitle='Additional Info'>
          <p className='text-base-dark-blue'>{additional_info || 'N/A'}</p>
        </AcademiaSectionWrapper>
      </div>
    </ParentModal>
  );
};

export default AppointmentDetailsModal;
